import { getJobsListJobsQueryKey } from "@api/generated/jobs/jobs";
import type { Catalog, CatalogRepeatSet, QueryDraft, QuerySpecIn, SavedQuery } from "@api/generated/model";
import {
  getQuerySavedQueryKey,
  useQueryCatalog,
  useQueryCreateSaved,
  useQueryDeleteSaved,
  useQueryExport,
  useQueryRun,
  useQuerySaveCells,
  useQuerySaved,
  useQueryUpdateSaved,
} from "@api/generated/query/query";
import { useUserMe } from "@api/generated/user/user";
import { Icon } from "@app/icons";
import { hasRole, useAutosave, useSeason, useSettings } from "@app/workspace";
import { Columns } from "@features/queries/Columns";
import { Conditions } from "@features/queries/Conditions";
import {
  codes,
  conditionFields,
  type Draft,
  defaultOp,
  emptyDraft,
  fieldIndex,
  fromSpec,
  lineSetParts,
  type Node,
  newCondition,
  removeColumn,
  toSpec,
  updateNode,
  withColumn,
} from "@features/queries/draft";
import { type PendingEdit, withEdit } from "@features/queries/edit";
import { FieldList } from "@features/queries/FieldList";
import { LineSetEditor } from "@features/queries/LineSetEditor";
import { ResultTable } from "@features/queries/ResultTable";
import { querySnapshot } from "@features/queries/snapshot";
import "@features/queries/query.css";
import { ResultBanner } from "@features/system/undo";
import {
  Button,
  Checkbox,
  Group,
  Kbd,
  Loader,
  Menu,
  Modal,
  MultiSelect,
  SegmentedControl,
  Select,
  Stack,
  TagsInput,
  Textarea,
  TextInput,
  Tooltip,
} from "@mantine/core";
import { useHotkeys } from "@mantine/hooks";
import { notifications } from "@mantine/notifications";
import { ConfirmModal } from "@shell/Dialogs";
import { useQueryClient } from "@tanstack/react-query";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { formatNumber, shortTime } from "@ui/format";
import { PageHeader } from "@ui/PageHeader";
import { refreshEdits } from "@ui/recentEdits";
import { Splitter } from "@ui/Splitter";
import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";

const search = z.object({ q: z.number().int().optional(), view: querySnapshot.optional() });

export const Route = createFileRoute("/trials/queries")({ validateSearch: search, component: QueryBuilderPage });

type Dialog = "save" | "delete" | "replicates" | null;

/** Waits for the catalog and the user's settings, which hold the draft being edited (it follows the user to any
 *  browser or computer). */
function QueryBuilderPage() {
  const settings = useSettings();
  const { q, view } = Route.useSearch();
  const catalog = useQueryCatalog({ query: { staleTime: Number.POSITIVE_INFINITY } });
  if (!settings || !catalog.data) return <Loader size="sm" m="md" />;
  return (
    <QueryBuilder
      key={`${q ?? "new"}:${JSON.stringify(view)}`}
      catalog={catalog.data}
      stored={view ? { q: q ?? null, spec: view as unknown as Record<string, unknown> } : (settings.query_draft ?? null)}
    />
  );
}

function QueryBuilder({ catalog, stored }: { catalog: Catalog; stored: QueryDraft | null }) {
  const { t } = useTranslation();
  const { q, view } = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const client = useQueryClient();
  const season = useSeason();
  const saved = useQuerySaved();
  const current = saved.data?.find((s) => s.id === q);
  const fields = useMemo(() => fieldIndex(catalog.tables), [catalog.tables]);
  const options = useMemo(
    () =>
      catalog.tables.map((tb) => ({
        group: tb.label,
        items: tb.fields.map((f) => ({ value: f.id, label: `${tb.label} · ${f.label}` })),
      })),
    [catalog.tables],
  );
  const ops = useMemo(() => ({ operators: catalog.operators, no_value: catalog.no_value }), [catalog]);

  // the stored draft continues where the user left off when it belongs to this page (the same ?q=)
  const [init] = useState(() => (stored && (stored.q ?? undefined) === q ? stored : null));
  const [draft, setDraft] = useState<Draft>(() => (init ? fromSpec(init.spec as QuerySpecIn) : emptyDraft(season.ev ? [season.ev] : [])));
  // the saved query the draft was loaded from; a different ?q= loads that one
  const [loaded, setLoaded] = useState<number | undefined>(init ? q : undefined);
  const run = useQueryRun();
  const [ranAt, setRanAt] = useState<string | null>(null);
  const [dialog, setDialog] = useState<Dialog>(null);
  const [lineSetOpen, setLineSetOpen] = useState(false);
  // correcting result cells (the legacy Show Selection grid was an updatable dynaset): breeders only
  const canEdit = hasRole(useUserMe().data?.roles, "nemesito");
  const [editing, setEditing] = useState(false);
  const [pending, setPending] = useState<ReadonlyMap<string, PendingEdit>>(new Map());
  const [corrected, setCorrected] = useState<{ count: number; operation: string | null } | null>(null);

  if (!view && current && loaded !== current.id) {
    setLoaded(current.id);
    setDraft(fromSpec(current.spec as QuerySpecIn));
    run.reset();
  }

  const spec = useMemo(() => toSpec(draft, fields), [draft, fields]);
  const specJson = useMemo(() => JSON.stringify(spec), [spec]);
  const savedJson = useMemo(() => current && JSON.stringify(toSpec(fromSpec(current.spec as QuerySpecIn), fields)), [current, fields]);
  const dirty = !!current && savedJson !== specJson;

  const draftValue = useMemo(() => ({ q: q ?? null, spec: spec as unknown as Record<string, unknown> }), [q, spec]);
  useAutosave("query_draft", draftValue, stored);

  const [initialOpen] = useState(() =>
    [...new Set([...draft.columns, ...conditionFields(spec.where)].map((id) => fields.get(id)?.table))].filter((x): x is string => !!x),
  );

  const execute = (edit = editing, query = spec) =>
    run.mutate(
      { data: { spec: query, edit } },
      {
        onSuccess: () => {
          setRanAt(shortTime(new Date().toISOString()));
          setPending(new Map()); // the rows may have moved: corrections belong to the result they were typed in
        },
      },
    );
  useHotkeys([["mod+Enter", () => execute()]], []);
  const saveCells = useQuerySaveCells({
    mutation: {
      onSuccess: (saved) => {
        setCorrected({ count: saved.updated, operation: saved.operation ?? null });
        refreshEdits(client);
        execute(true);
      },
    },
  });
  const onEdit = useCallback(
    (target: Parameters<typeof withEdit>[1], old: unknown, kind: string, text: string | null) =>
      setPending((p) => withEdit(p, target, old, kind, text)),
    [],
  );
  const edits = useMemo(() => ({ pending, onEdit }), [pending, onEdit]);

  const patch = (p: Partial<Draft>) => setDraft((d) => ({ ...d, ...p }));
  const onNode = useCallback(
    (key: string, fn: (n: Node) => Node | null) => setDraft((d) => ({ ...d, where: updateNode(d.where, key, fn) })),
    [],
  );
  const append = useCallback((node: Node) => setDraft((d) => ({ ...d, where: { ...d.where, items: [...d.where.items, node] } })), []);
  const toggleColumn = useCallback(
    (id: string) => setDraft((d) => (d.columns.includes(id) ? removeColumn(d, id) : { ...d, columns: withColumn(d.columns, id) })),
    [],
  );
  const addCondition = useCallback((id: string) => append(newCondition(id, defaultOp(fields.get(id)))), [append, fields]);

  const refreshSaved = () => client.invalidateQueries({ queryKey: getQuerySavedQueryKey() });
  const onSaved = (r: SavedQuery) => {
    notifications.show({ color: "green", message: t("queries.saved", { name: r.name }) });
    setLoaded(r.id);
    void refreshSaved();
  };
  const create = useQueryCreateSaved({
    mutation: {
      onSuccess: (r) => {
        onSaved(r);
        setDialog(null);
        void navigate({ search: { q: r.id } });
      },
    },
  });
  const update = useQueryUpdateSaved({ mutation: { onSuccess: onSaved } });
  const remove = useQueryDeleteSaved({
    mutation: {
      onSuccess: () => {
        setDialog(null);
        setLoaded(undefined);
        void refreshSaved();
        void navigate({ search: {} });
      },
    },
  });
  const exporting = useQueryExport({
    mutation: {
      onSuccess: ({ job_id }) => {
        void client.invalidateQueries({ queryKey: getJobsListJobsQueryKey() });
        notifications.show({
          color: "green",
          message: (
            <>
              {t("queries.exportStarted")}{" "}
              <Link to="/system/jobs" search={{ job: job_id }}>
                {t("nav.system.jobs")}
              </Link>
            </>
          ),
        });
      },
    },
  });
  /** The whole result as .xlsx in the background; ``perReplicate``: one row per replicate of that repeat set. */
  const exportAs = (perReplicate: string | null) =>
    exporting.mutate(
      {
        data: {
          spec,
          name: current?.name ?? t("queries.untitled"),
          expected_rows: run.data?.total,
          per_replicate: perReplicate,
        },
      },
      { onSuccess: () => setDialog(null) },
    );
  const saveCurrent = (s: SavedQuery, shared = s.shared) =>
    update.mutate({ queryId: s.id, data: { name: s.name, description: s.description, shared, spec } });

  const open = (s: SavedQuery | null) => {
    if (!s) {
      setDraft(emptyDraft(season.ev ? [season.ev] : []));
      setLoaded(undefined);
      run.reset();
    }
    void navigate({ search: { q: s?.id } });
  };

  const result = run.data;
  const mine = (saved.data ?? []).filter((s) => s.mine);
  const shared = (saved.data ?? []).filter((s) => !s.mine);

  return (
    <div className="page fill">
      <PageHeader
        title={current?.name ?? t("queries.newQuery")}
        pinSearch={{ view: spec }}
        item={current ? { kind: "query", title: current.name, subtitle: t("nav.trials.queries") } : undefined}
        subtitle={
          <span className="hstack" style={{ gap: 8 }}>
            {current && (
              <span className={`chip ${current.mine ? "g" : "b"}`}>
                {current.mine
                  ? t(current.shared ? "queries.savedShared" : "queries.savedOwn")
                  : t("queries.sharedBy", { name: current.owner })}
              </span>
            )}
            {dirty && <span className="chip y">{t(current?.mine ? "queries.modified" : "queries.modifiedCopy")}</span>}
            {ranAt && result && t("queries.lastRun", { time: ranAt, count: formatNumber(result.total) })}
          </span>
        }
        actions={
          <Group gap={8}>
            <Button size="xs" variant="default" leftSection={<Icon name="plus" size={14} />} onClick={() => open(null)}>
              {t("queries.newQuery")}
            </Button>
            <Menu position="bottom-end" width={300}>
              <Menu.Target>
                <Button size="xs" variant="default" rightSection={<Icon name="chevronDown" size={12} />}>
                  {t("queries.savedQueries")}
                </Button>
              </Menu.Target>
              <Menu.Dropdown mah={420} style={{ overflow: "auto" }}>
                {!mine.length && !shared.length && <Menu.Label>{t("queries.source.empty")}</Menu.Label>}
                {mine.length > 0 && <Menu.Label>{t("queries.mine")}</Menu.Label>}
                {mine.map((s) => (
                  <Menu.Item
                    key={s.id}
                    onClick={() => open(s)}
                    rightSection={s.shared ? <span className="chip b">{t("queries.shared")}</span> : null}
                  >
                    {s.name}
                  </Menu.Item>
                ))}
                {shared.length > 0 && <Menu.Label>{t("queries.sharedWithMe")}</Menu.Label>}
                {shared.map((s) => (
                  <Menu.Item key={s.id} onClick={() => open(s)} rightSection={<span className="sm mu">{s.owner}</span>}>
                    {s.name}
                  </Menu.Item>
                ))}
              </Menu.Dropdown>
            </Menu>
            {current?.mine && (
              <Button size="xs" variant="default" disabled={!dirty} loading={update.isPending} onClick={() => saveCurrent(current)}>
                {t("queries.save")}
              </Button>
            )}
            <Button
              size="xs"
              variant={current && !current.mine && dirty ? "filled" : "default"}
              onClick={() => setDialog("save")}
              title={current && !current.mine ? t("queries.saveCopyHelp", { name: current.owner }) : undefined}
            >
              {t(!current ? "queries.save" : current.mine ? "queries.saveAs" : "queries.saveCopy")}
            </Button>
            {current?.mine && (
              <>
                <Button
                  size="xs"
                  variant="default"
                  title={t(current.shared ? "queries.privateHelp" : "queries.publicHelp")}
                  onClick={() => saveCurrent(current, !current.shared)}
                >
                  {t(current.shared ? "queries.unshare" : "queries.share")}
                </Button>
                <Button size="xs" variant="default" c="var(--b-red)" onClick={() => setDialog("delete")}>
                  {t("queries.delete")}
                </Button>
              </>
            )}
            <Button size="xs" onClick={() => execute()} loading={run.isPending} rightSection={<Kbd size="xs">Ctrl ↵</Kbd>}>
              {t("queries.run")}
            </Button>
          </Group>
        }
      />

      <div className="fill-row">
        <FieldList
          tables={catalog.tables}
          columns={draft.columns}
          initialOpen={initialOpen}
          onToggleColumn={toggleColumn}
          onAddCondition={addCondition}
        />
        <Splitter cssVar="--q-aside-w" side="start" min={200} max={520} />
        <div className="grow stack" style={{ gap: 12, minWidth: 0, overflow: "auto" }}>
          <section className="card pad stack" style={{ gap: 12, flexShrink: 0 }}>
            <div className="hstack" style={{ gap: 16, alignItems: "flex-end", flexWrap: "wrap" }}>
              <MultiSelect
                size="xs"
                w={260}
                label={t("queries.years")}
                placeholder={draft.years.length ? undefined : t("queries.allYears")}
                data={season.years.map(String)}
                value={draft.years.map(String)}
                onChange={(v) => patch({ years: v.map(Number).sort((a, b) => b - a) })}
                clearable
              />
              <TagsInput
                size="xs"
                w={280}
                label={
                  <Tooltip label={t("queries.trialsHelp")} multiline w={300}>
                    <span className="hstack" style={{ gap: 4, display: "inline-flex" }}>
                      {t("queries.trials")}
                      <Icon name="help" size={12} />
                    </span>
                  </Tooltip>
                }
                placeholder={draft.trials.length ? undefined : t("queries.trialsPlaceholder")}
                value={draft.trials}
                onChange={(v) => patch({ trials: codes(v) })}
                splitChars={[",", ";", " ", "\n"]}
                acceptValueOnBlur
              />
              <TagsInput
                size="xs"
                w={180}
                label={t("queries.site")}
                placeholder={draft.sites.length ? undefined : t("queries.all")}
                value={draft.sites}
                onChange={(v) => patch({ sites: codes(v) })}
                splitChars={[",", ";", " "]}
              />
              {/* next to the scope they narrow, apart from each other (not pushed to the far edge) */}
              <div className="hstack" style={{ gap: 24, alignItems: "center", height: 30 }}>
                <Tooltip label={t("queries.genotypeSet.buttonHelp")} multiline w={300}>
                  <Button
                    size="xs"
                    variant={lineSetParts(draft.lineSet) ? "light" : "default"}
                    leftSection={<Icon name="sprout" size={14} />}
                    onClick={() => setLineSetOpen(true)}
                  >
                    {lineSetParts(draft.lineSet)
                      ? t("queries.genotypeSet.buttonActive", { count: lineSetParts(draft.lineSet) })
                      : t("queries.genotypeSet.button")}
                  </Button>
                </Tooltip>
                <Checkbox
                  size="xs"
                  label={t("queries.distinctRows")}
                  checked={draft.distinct}
                  disabled={!draft.columns.length}
                  title={draft.columns.length ? undefined : t("errors.distinct_needs_columns")}
                  onChange={(e) => patch({ distinct: e.currentTarget.checked })}
                />
              </div>
            </div>
            {lineSetOpen && (
              <LineSetEditor
                value={draft}
                ev={season.ev}
                seasons={season.years}
                exclude={current?.id}
                onApply={(change) => {
                  const next = { ...draft, ...change };
                  setDraft(next);
                  setLineSetOpen(false);
                  execute(editing, toSpec(next, fields)); // the list is there to be looked at: run it at once
                }}
                onClear={() => {
                  patch({ lineSet: null });
                  setLineSetOpen(false);
                }}
                onClose={() => setLineSetOpen(false)}
              />
            )}
            <div className="divider" />
            <Conditions
              where={draft.where}
              fields={fields}
              options={options}
              ops={ops}
              onNode={onNode}
              onAppend={append}
              onRootOp={(op) => setDraft((d) => ({ ...d, where: { ...d.where, op } }))}
              ancestorDepth={draft.ancestorDepth}
              onAncestorDepth={(ancestorDepth) => patch({ ancestorDepth })}
            />
            <div className="divider" />
            <div className="hstack" style={{ gap: 20, alignItems: "flex-start", flexWrap: "wrap" }}>
              <div className="grow" style={{ minWidth: 280 }}>
                <Columns
                  columns={draft.columns}
                  sort={draft.sort}
                  fields={fields}
                  onColumns={(columns) => patch({ columns })}
                  onSort={(sort) => patch({ sort })}
                  onRemove={toggleColumn}
                />
              </div>
              <div style={{ width: 320 }}>
                <span className="sec">{t("queries.joins")}</span>
                <div className="q-joins" style={{ marginTop: 8 }}>
                  {result ? (
                    result.joins.map((j, i) => (
                      <span key={j} className="hstack" style={{ gap: 4 }}>
                        {i > 0 && "→"}
                        <span className="chip b">{j}</span>
                      </span>
                    ))
                  ) : (
                    <span className="sm mu">{t("queries.joinsHelp")}</span>
                  )}
                </div>
              </div>
            </div>
          </section>

          <section className="card stack" style={{ gap: 0, flexGrow: 1, minHeight: 280, overflow: "hidden" }}>
            <div className="hstack" style={{ gap: 10, padding: "var(--b-row-padding)", borderBottom: "1px solid var(--b-border)" }}>
              <h2 className="h2">{t("queries.result")}</h2>
              {result && (
                <span className="chip g">
                  {t("queries.rowsAndTime", { count: formatNumber(result.total), seconds: formatNumber(result.millis / 1000, 1) })}
                </span>
              )}
              {result?.truncated && <span className="sm mu">{t("queries.truncated", { count: formatNumber(result.rows.length) })}</span>}
              {result?.line_set != null && (
                <span className="chip b">{t("queries.genotypeSet.result", { count: formatNumber(result.line_set) })}</span>
              )}
              <div className="grow" />
              {canEdit && (
                <Tooltip label={t("queries.edit.buttonHelp")}>
                  <Button
                    size="xs"
                    variant={editing ? "light" : "default"}
                    leftSection={<Icon name="edit" size={14} />}
                    onClick={() => {
                      const on = !editing;
                      setEditing(on);
                      setCorrected(null);
                      if (result && on && !result.edit) execute(true);
                      if (!on) setPending(new Map());
                    }}
                  >
                    {t("queries.edit.button")}
                  </Button>
                </Tooltip>
              )}
              {pending.size > 0 && (
                <>
                  <Button size="xs" variant="default" onClick={() => setPending(new Map())}>
                    {t("queries.edit.discard")}
                  </Button>
                  <Button
                    size="xs"
                    loading={saveCells.isPending}
                    onClick={() => saveCells.mutate({ data: { changes: [...pending.values()].map(({ key, text, ...cell }) => cell) } })}
                  >
                    {t("queries.edit.save", { count: pending.size })}
                  </Button>
                </>
              )}
              {result && !draft.columns.length && (
                <Button size="xs" variant="default" onClick={() => patch({ columns: result.columns.map((c) => c.id) })}>
                  {t("queries.useResultColumns")}
                </Button>
              )}
              <Button
                size="xs"
                variant="default"
                leftSection={<Icon name="download" size={14} />}
                loading={exporting.isPending}
                disabled={!result?.total}
                onClick={() => exportAs(null)}
              >
                {t("queries.excel")}
              </Button>
              {!!catalog.repeat_sets?.length && (
                <Button
                  size="xs"
                  variant="default"
                  leftSection={<Icon name="download" size={14} />}
                  loading={exporting.isPending}
                  disabled={!result?.total}
                  onClick={() => setDialog("replicates")}
                >
                  {t("queries.perReplicate.button")}
                </Button>
              )}
            </div>
            {corrected && (
              <ResultBanner operation={corrected.operation} onUndone={() => execute(editing)}>
                {t("queries.edit.done", { count: corrected.count })}
              </ResultBanner>
            )}
            {editing && result && (
              <p className="sm mu" style={{ padding: "4px 12px" }}>
                {result.edit ? t("queries.edit.help") : t("queries.edit.notEditable")}
              </p>
            )}
            {result ? (
              result.rows.length ? (
                <ResultTable result={result} edits={editing && result.edit ? edits : undefined} />
              ) : (
                <p className="mu sm" style={{ padding: 16 }}>
                  {t("queries.noRows")}
                </p>
              )
            ) : (
              <p className="mu sm" style={{ padding: 16 }}>
                {run.isPending ? <Loader size="xs" /> : t("queries.runHelp")}
              </p>
            )}
          </section>
        </div>
      </div>

      {dialog === "save" && (
        <SaveModal
          initial={current && !current.mine ? `${current.name} (${t("queries.copySuffix")})` : current ? `${current.name} 2` : ""}
          initialDescription={current?.description ?? ""}
          saving={create.isPending}
          onClose={() => setDialog(null)}
          onSave={(name, description, isShared) =>
            create.mutate({ data: { name, description: description || null, shared: isShared, spec } })
          }
        />
      )}
      {dialog === "replicates" && (
        <PerReplicateModal
          sets={catalog.repeat_sets ?? []}
          columns={result?.columns.map((c) => c.id) ?? []}
          exporting={exporting.isPending}
          onClose={() => setDialog(null)}
          onExport={exportAs}
        />
      )}
      {dialog === "delete" && current && (
        <ConfirmModal
          title={t("queries.delete")}
          text={t("queries.deleteConfirm", { name: current.name })}
          confirm={t("queries.delete")}
          danger
          loading={remove.isPending}
          onClose={() => setDialog(null)}
          onConfirm={() => remove.mutate({ queryId: current.id })}
        />
      )}
    </div>
  );
}

/** The legacy Excel (→Stat): the export with one row per replicate of a repeated measurement (Rep, Value), the
 *  query's other columns in front; the set the result already shows a replicate of is offered first. */
function PerReplicateModal(props: {
  sets: CatalogRepeatSet[];
  columns: string[];
  exporting: boolean;
  onClose: () => void;
  onExport: (set: string) => void;
}) {
  const { t } = useTranslation();
  const [chosen, setChosen] = useState(
    () =>
      (
        props.sets.find((s) => s.fields.some((f) => props.columns.includes(f))) ??
        props.sets.find((s) => s.id.startsWith("trms.")) ??
        props.sets[0]
      )?.id ?? null,
  );
  return (
    <Modal opened onClose={props.onClose} title={<b>{t("queries.perReplicate.title")}</b>}>
      <Stack>
        <p className="sm">{t("queries.perReplicate.help")}</p>
        <Select
          label={t("queries.perReplicate.set")}
          allowDeselect={false}
          value={chosen}
          onChange={setChosen}
          data={props.sets.map((s) => ({ value: s.id, label: s.label }))}
          comboboxProps={{ withinPortal: false }}
        />
        <Group justify="flex-end">
          <Button variant="default" onClick={props.onClose}>
            {t("shell.cancel")}
          </Button>
          <Button disabled={!chosen} loading={props.exporting} onClick={() => chosen && props.onExport(chosen)}>
            {t("queries.perReplicate.export")}
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}

function SaveModal({
  initial,
  initialDescription,
  saving,
  onSave,
  onClose,
}: {
  initial: string;
  initialDescription: string;
  saving: boolean;
  onSave: (name: string, description: string, shared: boolean) => void;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const [name, setName] = useState(initial);
  const [description, setDescription] = useState(initialDescription);
  const [isShared, setShared] = useState(false);
  return (
    <Modal opened onClose={onClose} title={<b>{t("queries.save")}</b>}>
      <Stack>
        <TextInput label={t("queries.name")} value={name} onChange={(e) => setName(e.currentTarget.value)} maxLength={100} data-autofocus />
        <Textarea
          label={t("queries.description")}
          value={description}
          onChange={(e) => setDescription(e.currentTarget.value)}
          maxLength={500}
          autosize
          minRows={2}
        />
        <Stack gap={4}>
          <span className="sm">
            <b>{t("queries.visibility")}</b>
          </span>
          <SegmentedControl
            size="xs"
            value={isShared ? "public" : "private"}
            onChange={(v) => setShared(v === "public")}
            data={[
              { value: "private", label: t("queries.private") },
              { value: "public", label: t("queries.public") },
            ]}
          />
          <span className="sm mu">{t(isShared ? "queries.publicHelp" : "queries.privateHelp")}</span>
        </Stack>
        <Group justify="flex-end">
          <Button variant="default" onClick={onClose}>
            {t("shell.cancel")}
          </Button>
          <Button disabled={!name.trim()} loading={saving} onClick={() => onSave(name.trim(), description.trim(), isShared)}>
            {t("queries.save")}
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
